import { Component, ElementRef, Inject, OnInit, Optional, ViewChild, ViewEncapsulation, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';

import { NavigationEnd, NavigationError, Router } from '@angular/router';

import { REQUEST } from '../express.tokens';
import { Request } from 'express';

import { appIconsList } from './app-icons';
import { CategoryService } from 'lib/services/category/category.service';
import { contains, scrollToTop, wait, getHeight } from 'lib/tools';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { InfiniteScrollService } from 'lib/services/infinite-scroll.service';
import { RouterService } from 'lib/services/router.service';
import { MarketoService } from 'lib/services/marketo.service';

const scriptName = 'app.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AppComponent implements OnInit {
    @ViewChild('infiniteScrollGlobalContainer')
    infiniteScrollGlobalContainer: ElementRef;
    isBrowser: boolean;

    constructor(
        @Optional() @Inject(REQUEST) protected req: Request,
        @Inject(PLATFORM_ID) private platformId: string,
        @Inject(DOCUMENT) private document: Document,
        @Inject('environment') private environment: any,
        private categoryService: CategoryService,
        private domSanitizer: DomSanitizer,
        private infiniteScrollService: InfiniteScrollService,
        private marketoService: MarketoService,
        private matIconRegistry: MatIconRegistry,
        private router: Router,
        private routerService: RouterService
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit() {
        try {
            this.initIcons();
            this.initInfiniteScroll();
            this.initRouteListener();
            if (!this.isBrowser) {
                return;
            }
            this.initChunkErrorHandler();
            getHeight();
            addEventListener('resize', () => {
                getHeight();
            });
        } catch (err) {
            console.error(...new ExxComError(538478, scriptName, err).stamp());
        }
    }

    private initIcons() {
        try {
            if (isPlatformServer(this.platformId)) {
                return;
            }
            const version = this.environment.version || '1.0.0';
            appIconsList.forEach((iconName: any) => {
                const path = `/assets/icons/${iconName}.svg?v=${version}`;
                const url = this.domSanitizer.bypassSecurityTrustResourceUrl(path);
                this.matIconRegistry.addSvgIcon(iconName, url);
            });
        } catch (err) {
            console.error(...new ExxComError(292881, scriptName, err).stamp());
        }
    }

    private initRouteListener() {
        try {
            this.routerService.onRouteChange('appComponent', (event: any) => {
                if (!this.isBrowser) {
                    return;
                }
                // Scroll to top of all except categories
                if (!contains(event.url, this.categoryService.paths)) {
                    scrollToTop(document.documentElement);
                    this.marketoService.init();
                    this.marketoService.initLoaded();
                }
            });
        } catch (err) {
            console.error(...new ExxComError(297839, scriptName, err).stamp());
        }
    }

    private initChunkErrorHandler() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationError) {
                const msg = event.error?.message || '';
                const isChunkError = msg.includes('Loading chunk') || msg.includes('dynamically imported module');
                if (isChunkError && !sessionStorage.getItem('chunkRetry')) {
                    sessionStorage.setItem('chunkRetry', 'true');
                    window.location.assign(event.url);
                }
            }
            if (event instanceof NavigationEnd) {
                sessionStorage.removeItem('chunkRetry');
            }
        });
    }

    isBlog() {
        try {
            if (this.isBrowser) {
                return contains(window.location.pathname, 'blog');
            } else {
                if (this.req == null) {
                    return false;
                }
                return contains(this.req.originalUrl, 'blog');
            }
        } catch (err) {
            console.error(...new ExxComError(729882, scriptName, err).stamp());
        }
    }

    async initInfiniteScroll() {
        try {
            if (!this.isBrowser) {
                return;
            }
            await wait('10ms');
            this.infiniteScrollService.initGlobalContainer(this.infiniteScrollGlobalContainer.nativeElement);
        } catch (err) {
            console.error(...new ExxComError(522992, scriptName, err).stamp());
        }
    }
}
